import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Snackbar,
  Alert,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";

import {
  PersonOutline,
  EmailOutlined,
  PhoneOutlined,
  LocationOnOutlined,
  SaveOutlined,
  RefreshOutlined,
  AdminPanelSettingsOutlined,
} from "@mui/icons-material";

import CertificateService from "../../services/certificateServices";

const initialValues = {
  name: "",
  email: "",
  mobile: "",
  address: "",
};

const CreateCertificateTeamLead = () => {
  const [values, setValues] = useState(initialValues);

  const [errors, setErrors] = useState({});

  const [submitting, setSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // --------------------------------------------------
  // Handle input change
  // --------------------------------------------------

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // --------------------------------------------------
  // Snackbar
  // --------------------------------------------------

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // --------------------------------------------------
  // Email validation
  // --------------------------------------------------

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // --------------------------------------------------
  // Form validation
  // --------------------------------------------------

  const validateForm = () => {
    const newErrors = {};

    // Name required
    if (!values.name.trim()) {
      newErrors.name = "Team Lead name is required";
    }

    // Email required + format validation
    if (!values.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(values.email.trim())) {
      newErrors.email = "Enter a valid email address";
    }

    // Mobile required only
    // No mobile format/length validation
    if (!values.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    }

    // Address required
    if (!values.address.trim()) {
      newErrors.address = "Address is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // --------------------------------------------------
  // Submit
  // --------------------------------------------------

  const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  try {
    setSubmitting(true);

    const payload = {
      name: values.name.trim(),
      email: values.email.trim(),
      mobile: values.mobile.trim(),
      address: values.address.trim(),
    };

    const response =
  await CertificateService.createCertificateTeamLead(payload);

    // Backend success message
    showSnackbar(
      response?.data?.message ||
        "Certificate Team Lead created successfully.",
      "success"
    );

    // Reset form after successful creation
    setValues(initialValues);
    setErrors({});
  } catch (error) {
    // Backend error response
    const backendMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      "Failed to create certificate team lead.";

    showSnackbar(backendMessage, "error");
  } finally {
    setSubmitting(false);
  }
};
  // --------------------------------------------------
  // Reset
  // --------------------------------------------------

  const handleReset = () => {
    setValues(initialValues);
    setErrors({});
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* --------------------------------------------- */}
      {/* Page Header */}
      {/* --------------------------------------------- */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 3,
        }}
      >
        <AdminPanelSettingsOutlined
          color="primary"
          sx={{ fontSize: 32 }}
        />

        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
          >
            Create Certificate Team Lead
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
          >
            Create a new certificate team lead account.
          </Typography>
        </Box>
      </Box>

      {/* --------------------------------------------- */}
      {/* Main Card */}
      {/* --------------------------------------------- */}

      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
        }}
      >

        <CardContent sx={{ p: 3 }}>
          {/* ----------------------------------------- */}
          {/* Three-column layout */}
          {/* ----------------------------------------- */}

          <Grid container spacing={3} alignItems="flex-start">

  {/* Certificate Team Lead Name */}
  <Grid item xs={12} sm={6} md={4}>
    <TextField
      fullWidth
      required
      label="Certificate Team Lead Name"
      name="name"
      value={values.name}
      onChange={handleChange}
      error={Boolean(errors.name)}
      helperText={errors.name || " "}
      placeholder="Enter certificate team lead name"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <PersonOutline />
            </InputAdornment>
          ),
        },
      }}
    />
  </Grid>

  {/* Email */}
  <Grid item xs={12} sm={6} md={4}>
    <TextField
      fullWidth
      required
      label="Email Address"
      name="email"
      type="email"
      value={values.email}
      onChange={handleChange}
      error={Boolean(errors.email)}
      helperText={errors.email || " "}
      placeholder="certificateteamlead@example.com"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <EmailOutlined />
            </InputAdornment>
          ),
        },
      }}
    />
  </Grid>

  {/* Mobile */}
  <Grid item xs={12} sm={6} md={4}>
    <TextField
      fullWidth
      required
      label="Mobile Number"
      name="mobile"
      value={values.mobile}
      onChange={(event) => {
      const value = event.target.value.replace(/\D/g, "");

      if (value.length <= 10) {
        setValues((prev) => ({
          ...prev,
          mobile: value,
        }));

        setErrors((prev) => ({
          ...prev,
          mobile: "",
        }));
      }
    }}
      error={Boolean(errors.mobile)}
      helperText={errors.mobile || " "}
      placeholder="Enter mobile number"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <PhoneOutlined />
            </InputAdornment>
          ),
        },
      }}
    />
  </Grid>

  {/* Address */}
  <Grid item xs={12} sm={12} md={4}>
    <TextField
      fullWidth
      required
      multiline
      rows={4}
      label="Address"
      name="address"
      value={values.address}
      onChange={handleChange}
      error={Boolean(errors.address)}
      helperText={errors.address || " "}
      placeholder="Enter complete address"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                alignSelf: "flex-start",
                mt: 1,
              }}
            >
              <LocationOnOutlined />
            </InputAdornment>
          ),
        },
      }}
    />
  </Grid>

</Grid>

          <Divider sx={{ my: 4 }} />

          {/* ----------------------------------------- */}
          {/* Actions */}
          {/* ----------------------------------------- */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshOutlined />}
              onClick={handleReset}
              disabled={submitting}
            >
              Reset
            </Button>

            <Button
              variant="contained"
              startIcon={
                submitting ? (
                  <CircularProgress
                    size={18}
                    color="inherit"
                  />
                ) : (
                  <SaveOutlined />
                )
              }
              onClick={handleSubmit}
              disabled={submitting}
              sx={{
                minWidth: 180,
              }}
            >
              {submitting
                ? "Creating..."
                : "Create Certificate Team Lead"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* --------------------------------------------- */}
      {/* Snackbar */}
      {/* --------------------------------------------- */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateCertificateTeamLead;

